import { Fragment, useEffect, useRef, useState } from "react";
import Navbar from "./navbar/index";
import SideBar from "./sidebar";
import { Link, useLocation } from "react-router-dom";
import Chat from "modules/chat/components";
import BigWindow from "modules/chat/components/BigWindow";
import ChatLauncher from "modules/chat/components/ChatLauncher";
import { useChatLauncher } from "modules/chat/hooks/useChatLauncher";
import useScrollTop from "hooks/v2/useScrollTop";
import Menue from "./Menue";
import Settings from "./Settings";
import SmSideBar from "./SmSideBar";
import { LANGUAGE } from "services/general";
import { LayoutContext } from "hooks/useLayout";
import { PermissionDebugToggle } from "components/shared/PermissionDebugger";
import { HAS_PERMISSION } from "services/user";
import { AI_MESSAGES_LIST } from "permissions/ai";
import { BreadcrumbProvider, Breadcrumbs } from "components/shared/breadcrumbs";
import { useTranslation } from "react-i18next";
import useTenantLogo from "hooks/useTenantLogo";
import IdleLogoutModal from "./IdleLogoutModal";

const AppLayout = ({ children }) => {
  const { t } = useTranslation();
  const tenantLogo = useTenantLogo();
  let [isChatOpen, setIsChatOpen] = useState(false);
  let [bigWindow, setBigWindow] = useState(false);
  const chatLauncher = useChatLauncher();
  let [isDark, setIsDark] = useState(+localStorage.getItem("isDark"));
  let [isOpen, setIsOpen] = useState(false);
  let [navPosition, setNavPosition] = useState(
    localStorage.getItem("navPosition") || "right"
  );

  let [sidebar, setSidebar] = useState(
    +localStorage.getItem("sidebar") || false
  );
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  let { pathname } = useLocation();
  useScrollTop(pathname);

  const handleChangePosition = () => {
    setSidebar(!sidebar);
    localStorage.setItem("sidebar", !sidebar ? 1 : 0);
  };

  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebar(false);
    }
  }, []);

  // Update logo state on window resize so it responds to smaller screens
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const mainScrollTimeout = useRef(null);
  const [mainScrolling, setMainScrolling] = useState(false);

  const handleMainScroll = () => {
    setMainScrolling(true);
    if (mainScrollTimeout.current) clearTimeout(mainScrollTimeout.current);
    mainScrollTimeout.current = setTimeout(() => setMainScrolling(false), 1500);
  };

  useEffect(() => {
    return () => {
      if (mainScrollTimeout.current) clearTimeout(mainScrollTimeout.current);
    };
  }, []);

  const isLargeScreen = windowWidth >= 1024;
  const showFullLogo = (isLargeScreen && !sidebar) || navPosition === "top";
  return (
    <LayoutContext.Provider value={{ navPosition, sidebar, isDark }}>
      <div className="flex flex-col h-screen overflow-hidden w-full relative">
        {/* relative: establishes stacking context for the absolute logo link */}
        <Navbar
          handleChangePosition={handleChangePosition}
          isDark={isDark}
          isOpen={isOpen}
          navPosition={navPosition}
          setIsOpen={setIsOpen}
          sidebar={!sidebar}
        />

        <Link
          to={`/${LANGUAGE}`}
          className={`p-1.5 sm:p-2 absolute z-[100] bg-primary start-0 top-0 flex justify-center items-center transition-all duration-200 ease-in-out ${
            showFullLogo
              ? "w-44 sm:w-60 h-16 sm:h-20"
              : "w-16 sm:w-20 h-16 sm:h-20"
          }`}
        >
          {tenantLogo ? (
            <img
              className="max-h-full max-w-full h-auto w-auto object-contain"
              src={tenantLogo}
              alt={t("generalSet.tenantLogo.label")}
            />
          ) : (
            <img
              className="max-h-8 sm:max-h-12 w-auto max-w-[85%] object-contain"
              src="/amer-last-logo.svg"
              alt="Amer logo"
            />
          )}
        </Link>
        {navPosition === "top" ? <Menue isDark={isDark} /> : ""}
        <div className="flex bg-surface-muted flex-1 overflow-hidden">
          {navPosition === "right" ? (
            <Fragment>
              <div
                className={`
      transition-all duration-200 ease-in-out overflow-hidden
      ${!sidebar ? "opacity-100" : "w-0 opacity-0 pointer-events-none"}
    `}
              >
                <SideBar isDark={isDark} />
              </div>
              <div
                className={`
      transition-all duration-200 ease-in-out overflow-hidden
      ${!sidebar ? "w-0 opacity-0 pointer-events-none" : "opacity-100"}
    `}
              >
                <SmSideBar isDark={isDark} />
              </div>
            </Fragment>
          ) : (
            ""
          )}
          <div
            onScroll={handleMainScroll}
            className={`w-full min-w-0 pt-5 transition-all duration-200 ease-in-out overflow-y-scroll overflow-x-hidden bar bar-auto-hide ${mainScrolling ? "bar-scrolling" : ""} ${
              navPosition === "right"
                ? !sidebar
                  ? "lg:ps-[271px] lg:pe-[30px] px-4 sm:px-[4%]"
                  : "lg:pe-[30px] pe-4 sm:pe-[4%] lg:ps-[110px] ps-[calc(1rem+80px)] sm:ps-[calc(4%+80px)]"
                : "lg:px-[29px] px-4 sm:px-[4%]"
            }`}
          >
            <BreadcrumbProvider>
              <Breadcrumbs />
              {children}
            </BreadcrumbProvider>
            {HAS_PERMISSION(AI_MESSAGES_LIST) && (
              // The strip spans the page's full width, so it is click-through and only
              // the chat pieces inside it take clicks — otherwise it silently swallows any
              // button scrolled under it (a form's Next / Previous footer).
              <div
                className={`flex flex-col sticky lg:end-[30px] end-[4%] bottom-[30px] gap-[20px] z-[900] pointer-events-none [&>*]:pointer-events-auto ${
                  chatLauncher.isAtEnd ? "items-end" : "items-start"
                }`}
              >
                {bigWindow && <BigWindow setOpen={setBigWindow} />}
                <div
                  className={`chat-window-wrapper ${
                    isChatOpen && !bigWindow
                      ? "chat-window-open"
                      : "chat-window-closed"
                  }`}
                >
                  {isChatOpen && !bigWindow && (
                    <Chat setOpen={setIsChatOpen} setBigWindow={setBigWindow} />
                  )}
                </div>
                <ChatLauncher
                  isChatOpen={isChatOpen}
                  onToggle={() => setIsChatOpen(!isChatOpen)}
                  launcher={chatLauncher}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Settings
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        setIsDark={setIsDark}
        isDark={isDark}
        setNavPosition={setNavPosition}
        navPosition={navPosition}
        setSidebar={setSidebar}
        sidebar={sidebar}
      />
      <PermissionDebugToggle />
      <IdleLogoutModal />
    </LayoutContext.Provider>
  );
};
export default AppLayout;
